// Subscriber home — total dues banner, chit cards, recent activity.

import type { ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { orpc } from '../lib/orpc.js';
import { current_language } from '../i18n/index.js';
import { format_inr } from '../lib/format.js';
import { friendly_error } from '../lib/friendly-error.js';

interface DashChit {
  chit: {
    id: string;
    name_ta: string;
    name_en: string;
    monthly_subscription_paise: number;
    pool_size: number;
  };
  slot: number;
  total_paid_paise: number;
  total_expected_paise: number;
  next_due: {
    id: string;
    due_month: string;
    expected_amount_paise: number;
    total_paid_paise: number;
    status: string;
  } | null;
  upcoming_payout_month: string | null;
}

interface RecentPayment {
  payment_id: string;
  chit_id: string;
  chit_name_ta: string;
  chit_name_en: string;
  amount_paise: number;
  method: string;
  reference: string | null;
  timestamp: string;
  due_month: string;
}

export function SubHome(): ReactElement {
  const { t } = useTranslation();
  const lang = current_language();
  const q = useQuery({
    queryKey: ['sub-dashboard'],
    queryFn: () => orpc.sub.dashboard({}),
  });

  if (q.isLoading) return <div style={{ color: 'var(--ink-3)', fontSize: 13 }}>Loading…</div>;
  if (q.error) return <div style={{ color: 'var(--bad)', fontSize: 13 }}>{friendly_error(q.error)}</div>;
  if (!q.data) return <></>;

  const subscriber = q.data.subscriber as { name_ta: string; name_en: string };
  const chits = q.data.chits as DashChit[];
  const recent = q.data.recent_payments as RecentPayment[];

  return (
    <>
      {/* Greeting */}
      <div>
        <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>{t('sub.greeting', 'Hello')},</div>
        <div className={'h-title ' + (lang === 'ta' ? 'tamil' : '')} style={{ fontSize: 20 }}>
          {lang === 'ta' ? subscriber.name_ta : subscriber.name_en}
        </div>
      </div>

      {/* Total dues banner */}
      <div className="sub-hero">
        <div className="lbl">{t('sub.total_due', 'Total amount due')}</div>
        <div className="val">{format_inr(q.data.total_dues_paise)}</div>
        <div className="note">{t('sub.across_chits', '{{count}} active chits', { count: chits.length })}</div>
      </div>

      {/* Chit cards */}
      <div className="section-band">{t('sub.your_chits', 'Your chits')}</div>
      {chits.length === 0 && (
        <div className="card" style={{ padding: 22, textAlign: 'center', color: 'var(--ink-3)', fontSize: 13 }}>
          {t('sub.no_chits', 'You are not in any active chits.')}
        </div>
      )}
      {chits.map((c) => (
        <Link key={c.chit.id} to={`/sub/chit/${c.chit.id}`} className="card" style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 10, textDecoration: 'none', color: 'inherit' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
            <div>
              <div className={lang === 'ta' ? 'tamil' : ''} style={{ fontSize: 15, fontWeight: 600 }}>
                {lang === 'ta' ? c.chit.name_ta : c.chit.name_en}
              </div>
              <div style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>
                {t('sub.slot', 'Slot')} {c.slot} · {c.chit.pool_size} {t('sub.members', 'members')}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>{t('sub.monthly', 'Monthly')}</div>
              <div className="mono" style={{ fontSize: 13 }}>{format_inr(c.chit.monthly_subscription_paise)}</div>
            </div>
          </div>

          {c.next_due && (
            <div style={{ borderTop: '1px solid var(--line)', paddingTop: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
              <div>
                <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--ink-3)' }}>
                  {t('sub.next_due', 'Next due')} · {c.next_due.due_month}
                </div>
                <div className="mono" style={{ fontSize: 14 }}>
                  {format_inr(Math.max(0, c.next_due.expected_amount_paise - c.next_due.total_paid_paise))}
                </div>
              </div>
              {c.next_due.status !== 'paid' ? (
                <Link to={`/sub/pay/${c.next_due.id}`} onClick={(e) => e.stopPropagation()} className="btn primary sm">
                  {t('sub.pay_now', 'Pay now')}
                </Link>
              ) : (
                <span style={{ fontSize: 11, padding: '3px 8px', borderRadius: 5, background: 'var(--good-soft)', color: 'var(--good)' }}>
                  {t('sub.paid', 'Paid')}
                </span>
              )}
            </div>
          )}

          {c.upcoming_payout_month && (
            <div style={{ fontSize: 11.5, color: 'var(--info)', background: 'var(--info-soft)', padding: '5px 9px', borderRadius: 6 }}>
              {t('sub.payout_in', 'Payout scheduled for {{month}}', { month: c.upcoming_payout_month })}
            </div>
          )}
        </Link>
      ))}

      {/* Recent payments */}
      {recent.length > 0 && (
        <>
          <div className="section-band">{t('sub.recent', 'Recent payments')}</div>
          <div className="card sub-list" style={{ padding: 0 }}>
            {recent.slice(0, 6).map((p) => (
              <div key={p.payment_id} className="row">
                <div>
                  <div className={lang === 'ta' ? 'tamil' : ''} style={{ fontSize: 13 }}>
                    {lang === 'ta' ? p.chit_name_ta : p.chit_name_en}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>{p.due_month} · {p.method.toUpperCase()}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div className="mono" style={{ fontSize: 13, color: 'var(--good)' }}>+{format_inr(p.amount_paise)}</div>
                  <div style={{ fontSize: 10, color: 'var(--ink-3)' }}>{new Date(p.timestamp).toLocaleDateString()}</div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </>
  );
}
